import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CreditCard, Banknote, CheckCircle2, Sparkles, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBilling() {
  const { admin, getAdminAxios } = useOutletContext();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [transferDetails, setTransferDetails] = useState("");
  const [billing, setBilling] = useState(null);
  const [picked, setPicked] = useState(null);
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, pRes] = await Promise.all([
        getAdminAxios().get(`${API}/reseller/me/billing`),
        getAdminAxios().get(`${API}/reseller/me/plans`),
      ]);
      setBilling(bRes.data);
      setPlans(pRes.data.plans || []);
      setTransferDetails(pRes.data.transfer_details || "");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [getAdminAxios]);

  useEffect(() => { load(); }, [load]);

  // Root admin doesn't need this page
  useEffect(() => {
    if (admin?.is_root_super_admin) navigate("/admin/dashboard", { replace: true });
  }, [admin, navigate]);

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true); setError("");
    try {
      const { data } = await getAdminAxios().post(`${API}/reseller/me/subscribe`, { plan_slug: picked.slug, reference });
      setDone(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;
  }

  return (
    <div className="p-8 max-w-4xl" data-testid="admin-billing-page">
      <h1 className="text-2xl font-semibold text-white flex items-center gap-2 mb-1"><CreditCard className="w-6 h-6" /> Facturación</h1>
      <p className="text-sm text-slate-400 mb-6">Elegí un plan para activar / renovar tu cuenta de sub super admin.</p>

      {billing && (
        <Card className="bg-[#13112b] border-indigo-900/30 mb-6">
          <CardContent className="p-4 flex items-center gap-3 text-sm">
            <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
            <div className="text-slate-300">
              Plan actual: <span className="text-white font-medium">{billing.plan_name || "—"}</span>
              {billing.expires_on && <> · vence el <span className="text-white">{billing.expires_on.slice(0,10)}</span></>}
              {billing.max_clients != null && <> · {billing.max_clients} clientes incluidos</>}
            </div>
          </CardContent>
        </Card>
      )}

      {done ? (
        <Card className="bg-emerald-500/5 border-emerald-500/30" data-testid="billing-success">
          <CardContent className="p-6 text-center space-y-3">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" />
            <h2 className="text-lg text-white font-semibold">¡Solicitud enviada!</h2>
            <p className="text-sm text-slate-300">
              Tu solicitud de plan <strong className="text-white">{done.plan_name}</strong> está pendiente de aprobación.
              Apenas confirmemos la transferencia te activamos el plan.
            </p>
            <p className="text-xs text-slate-500">ID: <code className="bg-black/40 px-1 rounded">{done.id}</code></p>
            <Button onClick={() => { setDone(null); setPicked(null); load(); }} className="bg-amber-500 hover:bg-amber-600 text-black">
              Volver
            </Button>
          </CardContent>
        </Card>
      ) : !picked ? (
        <>
          <h2 className="text-lg text-white font-medium mb-3">Planes disponibles</h2>
          <div className="grid md:grid-cols-3 gap-3">
            {plans.length === 0 ? (
              <p className="text-sm text-slate-500 col-span-3">El super admin principal todavía no creó planes de reseller. Pedile que los configure.</p>
            ) : plans.map((p) => (
              <Card key={p.slug} className="bg-[#13112b] border-indigo-900/30 hover:border-amber-500/40 transition cursor-pointer" onClick={() => setPicked(p)} data-testid={`plan-card-${p.slug}`}>
                <CardHeader>
                  <CardTitle className="text-white text-base">{p.name}</CardTitle>
                  <CardDescription className="text-slate-400">{p.description || "—"}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-amber-400">${p.price}</p>
                  <p className="text-xs text-slate-400 mt-1">{p.currency} / {p.days} días</p>
                  <p className="text-xs text-slate-300 mt-2 flex items-center gap-1.5"><Users className="w-3 h-3" /> Hasta {p.max_clients} clientes</p>
                  <Button className="mt-4 w-full bg-amber-500 hover:bg-amber-600 text-black">Elegir</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <Card className="bg-[#13112b] border-indigo-900/30">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2"><Banknote className="w-4 h-4 text-emerald-400" /> Pago por transferencia</CardTitle>
            <CardDescription className="text-slate-400">
              Plan elegido: <strong className="text-white">{picked.name}</strong> · <span className="text-amber-300">${picked.price} {picked.currency}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {transferDetails ? (
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Datos para transferir</Label>
                <pre className="text-xs whitespace-pre-wrap font-mono text-slate-200 bg-black/40 p-3 rounded border border-white/10 mt-1" data-testid="billing-transfer-details">
                  {transferDetails}
                </pre>
              </div>
            ) : (
              <p className="text-xs text-amber-300">⚠️ El super admin principal todavía no cargó los datos de transferencia. Pedíselo antes de pagar.</p>
            )}
            <div>
              <Label className="text-slate-300">Referencia / comprobante (opcional)</Label>
              <Textarea value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ej: Transferencia desde Banco Galicia el 12/05, $99 USD" rows={3} className="bg-white/5 border-white/10 text-white" data-testid="billing-reference" />
            </div>
            {error && <p className="text-xs text-red-300">⚠️ {error}</p>}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPicked(null)} className="border-white/10 text-slate-300">Cambiar plan</Button>
              <Button onClick={submit} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="billing-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Enviar solicitud
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
